import React, { useEffect, useRef } from 'react';
import { C } from './UI';

// Canvas-based signature capture — genuinely new in this codebase. The backend contract
// (SignatureBase64Png + geo) already exists and is proven via WorkOrder's contractor-signs
// flow, but nothing on the web side has ever produced that payload before; this is the first.
// Scales pointer coordinates by the canvas's actual pixel size vs its CSS-rendered size so
// drawing stays accurate when the canvas is stretched to fit a narrow phone screen — the most
// likely place this gets used, since a prospect signs an estimate on their own device.

interface Props {
    onChange: (dataUrl: string | null) => void;
    height?: number;
}

export function SignaturePad({ onChange, height = 160 }: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawing = useRef(false);
    const hasDrawn = useRef(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Render at 2x for a crisper line, scaled back down via CSS.
        canvas.width = 600;
        canvas.height = height * 2;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.strokeStyle = '#1a1a1a';
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        }
    }, [height]);

    const getPoint = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const raw = 'touches' in e ? e.touches[0] : (e as React.MouseEvent);
        if (!raw) return null;
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return { x: (raw.clientX - rect.left) * scaleX, y: (raw.clientY - rect.top) * scaleY };
    };

    const start = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        const ctx = canvasRef.current?.getContext('2d');
        const p = getPoint(e);
        if (!ctx || !p) return;
        drawing.current = true;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
    };

    const move = (e: React.MouseEvent | React.TouchEvent) => {
        if (!drawing.current) return;
        e.preventDefault();
        const ctx = canvasRef.current?.getContext('2d');
        const p = getPoint(e);
        if (!ctx || !p) return;
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        hasDrawn.current = true;
    };

    const end = () => {
        if (!drawing.current) return;
        drawing.current = false;
        if (hasDrawn.current && canvasRef.current) onChange(canvasRef.current.toDataURL('image/png'));
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        hasDrawn.current = false;
        onChange(null);
    };

    return (
        <div>
            <canvas
                ref={canvasRef}
                style={{ width: '100%', height, border: `1px solid ${C.border}`, borderRadius: 8, touchAction: 'none', background: '#fff', cursor: 'crosshair' }}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                <span style={{ fontSize: 11, color: C.textMuted }}>Sign above with your finger or mouse</span>
                <button onClick={clear} style={{ background: 'transparent', border: 'none', color: C.textSub, cursor: 'pointer', fontSize: 12 }}>Clear</button>
            </div>
        </div>
    );
}
